import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Avatar } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';

export interface Person {
  readonly id: string;
  readonly name: string;
  readonly detail?: string | undefined;
}

/** Ignora acento e caixa: quem digita "jose" tem de achar "José". */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Escolha de pessoas, fixa na página.
 *
 * Não é menu suspenso: a lista fica aberta, na mesma superfície do formulário,
 * com busca em cima e altura de cinco linhas — daí em diante rola. O painel
 * escuro que a barra de filtros usa é bom para recortar uma listagem e ruim
 * aqui, onde a escolha faz parte do que se está preenchendo e precisa conviver
 * com os outros campos.
 */
export function PeoplePicker({
  people,
  selected,
  onToggle,
  lockedIds = [],
  emptyLabel = 'Ninguém encontrado.',
}: {
  people: readonly Person[];
  selected: readonly string[];
  onToggle: (id: string, next: boolean) => void;
  /** Quem aparece marcado mas não pode ser desmarcado — o autor, por exemplo. */
  lockedIds?: readonly string[];
  emptyLabel?: string;
}) {
  const [term, setTerm] = useState('');

  const visible = useMemo(() => {
    if (term.trim() === '') return people;

    const needle = normalize(term);
    return people.filter((person) =>
      normalize(`${person.name} ${person.detail ?? ''}`).includes(needle),
    );
  }, [people, term]);

  return (
    <div className="overflow-hidden rounded-md bg-muted">
      <div className="relative">
        <Search
          size={15}
          strokeWidth={1.5}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar pessoa"
          aria-label="Buscar pessoa"
          className="h-10 w-full bg-transparent pr-9 pl-9 text-[13px] placeholder:text-muted-foreground"
        />
        {term ? (
          <button
            type="button"
            onClick={() => setTerm('')}
            aria-label="Limpar busca"
            className="absolute top-1/2 right-2 grid size-6 -translate-y-1/2 place-items-center rounded-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X size={13} strokeWidth={2} aria-hidden />
          </button>
        ) : null}
      </div>

      {/* Cinco linhas de 44px cabem sem rolar; o resto rola. */}
      <ul className="max-h-[220px] overflow-y-auto p-1">
        {visible.length === 0 ? (
          <li className="px-2.5 py-3 text-[13px] text-muted-foreground">{emptyLabel}</li>
        ) : (
          visible.map((person) => {
            const checked = selected.includes(person.id);
            const locked = lockedIds.includes(person.id);

            return (
              <li key={person.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  disabled={locked}
                  onClick={() => onToggle(person.id, !checked)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors',
                    locked ? 'cursor-default opacity-70' : 'hover:bg-accent',
                  )}
                >
                  <span
                    className={cn(
                      'grid size-[18px] shrink-0 place-items-center rounded-2xs transition-colors',
                      checked ? 'bg-primary' : 'bg-card',
                    )}
                    aria-hidden
                  >
                    {checked ? (
                      <svg viewBox="0 0 12 12" className="size-3 text-primary-foreground">
                        <path
                          d="M2 6.5 4.5 9 10 3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </span>

                  <Avatar name={person.name} url={null} size={22} />

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px]">{person.name}</span>
                    {person.detail ? (
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {person.detail}
                      </span>
                    ) : null}
                  </span>

                  {locked ? (
                    <span className="shrink-0 text-[11px] text-muted-foreground">autor</span>
                  ) : null}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
